import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import classNames from 'classnames';
import './styles.scss';

// Matches the `meta` shape consumers already use with the flat Table molecule, so a consumer can
// swap between Table and MatrixTable without rewriting column definitions.
interface CustomMeta {
  headerProps?: {
    className?: string;
    style?: CSSProperties;
  };
  cellProps?: {
    className?: string;
    style?: CSSProperties;
  };
}

export interface MatrixTableProps<T extends object = Record<string, unknown>> {
  columns: ColumnDef<T>[];
  data: T[];
  className?: string;
  noData?: string | JSX.Element;
}

export const MatrixTable = <T extends object = Record<string, unknown>>(props: MatrixTableProps<T>) => {
  const { columns, data, className = '', noData } = props;

  const { getHeaderGroups, getRowModel } = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0 && noData) {
    return <div className={classNames('matrix-table', className)}>{noData}</div>;
  }

  const headerGroups = getHeaderGroups();
  // Flat columns (no sub-columns) alongside grouped columns appear twice in header groups:
  // as a placeholder in the top row and as the "real" header in the bottom row. We render
  // them once at the top with rowSpan so the label visually fills both header rows, then
  // skip the real copy in lower rows.
  const hoistedColumnIds = new Set<string>(
    headerGroups[0]?.headers.filter((h) => h.isPlaceholder).map((h) => h.column.id) ?? []
  );

  return (
    <div className={classNames('matrix-table', className)}>
      <table>
        <thead>
          {headerGroups.map((headerGroup, rowIdx) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as CustomMeta | undefined;
                const colSpan = header.colSpan > 1 ? header.colSpan : undefined;

                if (header.isPlaceholder) {
                  if (rowIdx !== 0) {
                    return null;
                  }
                  return (
                    <th
                      key={header.id}
                      colSpan={colSpan}
                      rowSpan={headerGroups.length}
                      className={meta?.headerProps?.className}
                      style={meta?.headerProps?.style}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                }

                if (rowIdx > 0 && hoistedColumnIds.has(header.column.id)) {
                  return null;
                }

                return (
                  <th
                    key={header.id}
                    colSpan={colSpan}
                    className={meta?.headerProps?.className}
                    style={meta?.headerProps?.style}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as CustomMeta | undefined;
                return (
                  <td
                    key={cell.id}
                    className={meta?.cellProps?.className}
                    style={meta?.cellProps?.style}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
